import React, { useEffect, useState } from 'react';
import { Outlet, useBlocker } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppAlert from '../AppAlert/AppAlert';
import Collapse from '@/components/Collapse/Collapse';
import { SkipContentLink } from '@/components/SkipContentLink/SkipContentLink';
import TopBar from '@/components/TopBar/TopBar';
import NavigationMenu from '@/nav/components/NavigationMenu/NavigationMenu';
import { useTablet } from '@/hooks/responsiveHooks';
import useRouteHandle from '@/hooks/useRouteHandle';
import { useAlertContext } from '@/core/providers/AlertContext';
import ConfirmationDialog from '@/components/AppDialog/ConfirmationDialog';
import { useUnsavedConfirmationContext } from '@/core/providers/UnsavedConfirmationContext';
import SystemAlert from '@/components/SystemAlert/SystemAlert';
import { useNotificationAlertContext } from '@/core/providers/SystemAlertContext';

function AppLayout() {
  const theme = useTheme();
  const isTablet = useTablet();
  const [navExpanded, setNavExpanded] = useState(!isTablet);
  const isFullPage = useRouteHandle('isFullPage') ?? false;
  const hideTopBar = useRouteHandle('hideTopBar') ?? false;
  const isFullWidth = useRouteHandle('isFullWidth') ?? false;
  const useSecondaryBackground = useRouteHandle('useSecondaryBackground') ?? false;

  const {
    isAlertActive,
    contextAlertOptions: {
      custom: { withTransition, description: alertDescription },
      app: { withMain: alertWithMain, title: alertTitle },
      mui: muiAlertProps,
    },
  } = useAlertContext();

  const {
    isSystemAlertActive,
    contextSystemAlertOptions: { description: systemAlertDescription, title: systemAlertTitle },
  } = useNotificationAlertContext();

  const {
    isUnsavedConfirmationActive,
    setIsUnsavedConfirmationActive,
    contextUnsavedConfirmationOptions,
  } = useUnsavedConfirmationContext();

  const blocker = useBlocker(() => isUnsavedConfirmationActive);

  useEffect(() => {
    setNavExpanded(!isTablet);
  }, [isTablet]);

  return (
    <>
      <SkipContentLink href="#main-content">Skip to main content</SkipContentLink>
      <Box
        display="flex"
        flexWrap="nowrap"
        flexDirection="column"
        marginLeft="auto"
        marginRight="auto"
        height="100%"
        maxWidth={theme.app.maxWidth}
        id="main-container"
        sx={{
          overflowY: 'hidden',
          backgroundColor: useSecondaryBackground ? 'custom.background' : 'common.white',
        }}
      >
        <Collapse open={isSystemAlertActive} withTransition={true}>
          <SystemAlert description={systemAlertDescription} title={systemAlertTitle} />
        </Collapse>
        <Collapse open={isAlertActive} withTransition={withTransition}>
          <AppAlert title={alertTitle} withMain={alertWithMain} {...muiAlertProps}>
            {alertDescription}
          </AppAlert>
        </Collapse>

        <Box
          display="flex"
          flex={1}
          height="100%"
          sx={{
            overflowY: 'hidden',
          }}
        >
          {!isFullPage ? (
            <NavigationMenu expanded={navExpanded} setExpanded={setNavExpanded} />
          ) : null}
          <Box
            display="flex"
            pl={isFullWidth ? 0 : 6}
            pr={isFullWidth ? 0 : 6}
            flexDirection="column"
            flexWrap="nowrap"
            height="100%"
            width="100%"
            sx={{ overflowY: 'auto' }}
          >
            <ConfirmationDialog
              open={blocker.state === 'blocked'}
              setVisible={() => blocker.reset?.()}
              label={contextUnsavedConfirmationOptions.label}
              yesLabel={contextUnsavedConfirmationOptions.yesLabel}
              cancelLabel={contextUnsavedConfirmationOptions.cancelLabel}
              switchLabelTheme
              onYes={() => {
                setIsUnsavedConfirmationActive(false);
                blocker.proceed?.();
              }}
            />
            {!isFullPage ? hideTopBar ? null : <TopBar /> : null}
            <Outlet />
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default AppLayout;
